import type React from 'react';
import { useEffect, useMemo } from 'react';
import { cn } from '../../../lib/utils.ts';
import type { ExplorerSort, ExplorerViewFilters } from '../../../server/utils/explorer-utils.ts';
import {
    ExplorerTopLevelEntity,
    allExplorerTabs,
    getExplorerFilterKeyByTabName,
    shouldHideUnselectedRows,
} from '../../../server/utils/explorer-utils.ts';
import { useAcl, useAppConfig } from '../../acl/acl-provider.tsx';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import type { AnalyticsEventPayload } from '../../analytics/types/analytics-events.ts';
import {
    clearedExplorerSearches,
    defaultExplorerFilters,
    useExplorerView,
} from '../hooks/use-explorer-query-params.ts';
import { ExplorerFilters } from './explorer-filters.tsx';
import { ExplorerTopLevelEntityTabs } from './explorer-top-level-entity-tabs.tsx';
import { ExplorerViewAppliedFilters } from './explorer-view-applied-filters.tsx';
import { ExplorerViewClerk } from './explorer-view-clerk.tsx';
import { ExplorerViewCustomers } from './explorer-view-customers.tsx';
import { ExplorerViewIndustry } from './explorer-view-industry.tsx';
import { ExplorerViewNormalisedCategory } from './explorer-view-normalised-category.tsx';
import { ExplorerViewProductGroup } from './explorer-view-product-group.tsx';
import { ExplorerViewProduct } from './explorer-view-product.tsx';
import { ExplorerViewSalesReps } from './explorer-view-sales-reps.tsx';
import { ExplorerViewStorageLocations } from './explorer-view-storage-locations.tsx';
import { ExplorerViewSupplier } from './explorer-view-supplier.tsx';

type Props = {
    pageSource: AnalyticsEventPayload<'sales_report_viewed'>['page_source'];
    className?: string;
};

export function ExplorerView({ pageSource, className = '' }: Props) {
    const { state, setState, defaultFilters: overrideFilters } = useExplorerView();

    const acl = useAcl();

    const appConfig = useAppConfig();

    const sendEvent = useAnalytics();

    useEffect(() => {
        sendEvent('sales_report_viewed', {
            page_source: pageSource,
        });
    }, [pageSource]);

    const tabsToShow = useMemo(
        // eslint-disable-next-line react-hooks/preserve-manual-memoization -- legacy code
        () =>
            allExplorerTabs.filter((tab: ExplorerTopLevelEntity) => {
                const filterKey = getExplorerFilterKeyByTabName(tab);

                if (
                    tab === ExplorerTopLevelEntity.NORMALISED_CATEGORY &&
                    appConfig.normalisedCategoryDepthsUsedInInsightsInExplorer === null
                ) {
                    // Hide normalised category tab when no category depths are found in insights
                    return false;
                }

                if (tab === ExplorerTopLevelEntity.SALES_REP && 'customer' in overrideFilters) {
                    // Because when customer is selected there is no use for sales rep tab
                    return false;
                }

                // We need to hide the product tab
                // apps/webapp/src/features/inventory/components/inventory-details.tsx
                if (
                    (tab === ExplorerTopLevelEntity.PRODUCT_GROUP ||
                        tab === ExplorerTopLevelEntity.PRODUCT) &&
                    'product' in overrideFilters
                ) {
                    // Because when product is selected there might be only one product group
                    return false;
                }

                return acl.can('filter', 'SalesReportFilter', filterKey);
            }),
        [acl, overrideFilters],
    );

    // -----------------------------
    // Selected values
    // -----------------------------

    const {
        tab: selectedTab,
        searchString,
        window: selectedWindow,
        customDateRange: selectedCustomDateRange,
        billing: selectedBillingStatus,
        sorting: selectedSortingFilter,
        targetUnit: selectedTargetUnit,
        yearPeriods: selectedYearPeriod,
        ...queryFilters
    } = state;

    const setAppliedFilters = (filters: ExplorerViewFilters) => {
        const usedFilters = (Object.keys(filters) as (keyof ExplorerViewFilters)[]).filter(
            (key) => filters[key].length > 0,
        );

        sendEvent('sales_report_filters_applied', {
            filters: usedFilters,
            number_of_filters: usedFilters.length,
        });

        setState((prevState) => ({
            ...prevState,
            ...filters,
        }));
    };

    const setSelectedTab = (tab: ExplorerTopLevelEntity) => {
        setState((prevState) => ({ ...prevState, ...clearedExplorerSearches, tab }));
    };

    // One write: `setState` reads the current params from a ref, so a second call
    // in the same handler would lose the first.
    const removeAllFilters = () => {
        sendEvent('sales_report_filters_applied', {
            filters: [],
            number_of_filters: 0,
        });

        setState((prevState) => ({
            ...prevState,
            ...defaultExplorerFilters,
            ...clearedExplorerSearches,
        }));
    };

    const setSortingFilter = (sorting: ExplorerSort) => {
        setState((prevState) => ({
            ...prevState,
            sorting: sorting ?? undefined,
        }));
    };

    const combinedFilters = {
        ...queryFilters,
        ...overrideFilters,
    };

    const commonTabProps = {
        searchString,
        window: selectedWindow,
        customDateRange: selectedCustomDateRange,
        filters: combinedFilters,
        billingStatus: selectedBillingStatus,
        sortingFilter: selectedSortingFilter,
        yearPeriods: selectedYearPeriod,
        setSortingFilter,
    } satisfies Omit<React.ComponentProps<typeof ExplorerViewStorageLocations>, 'onFiltersChange'>;

    return (
        <div className={cn('flex h-full w-full flex-col gap-3', className)}>
            <ExplorerFilters />

            <ExplorerTopLevelEntityTabs
                selectedTab={selectedTab}
                onTabChange={setSelectedTab}
                tabsToShow={tabsToShow}
            />

            <ExplorerViewAppliedFilters
                className="mb-0"
                tabsToShow={tabsToShow}
                filters={queryFilters}
                onFiltersChange={setAppliedFilters}
                onRemoveAllFilters={removeAllFilters}
            />

            <div className={cn('flex flex-col grow-0 overflow-y-hidden')}>
                {selectedTab === ExplorerTopLevelEntity.STORAGE_LOCATION && (
                    <ExplorerViewStorageLocations
                        {...commonTabProps}
                        onFiltersChange={(value) => {
                            setAppliedFilters({
                                ...queryFilters,
                                storageLocation: value,
                            });
                        }}
                        key={selectedWindow}
                    />
                )}

                {selectedTab === ExplorerTopLevelEntity.SALES_REP && (
                    <ExplorerViewSalesReps
                        {...commonTabProps}
                        onFiltersChange={(value) => {
                            setAppliedFilters({
                                ...queryFilters,
                                salesRep: value,
                            });
                        }}
                        key={selectedWindow}
                    />
                )}

                {selectedTab === ExplorerTopLevelEntity.CUSTOMER && (
                    <ExplorerViewCustomers
                        {...commonTabProps}
                        onFiltersChange={(value) => {
                            setAppliedFilters({
                                ...queryFilters,
                                customer: value,
                            });
                        }}
                        key={selectedWindow}
                        hideUnselectedRows={shouldHideUnselectedRows({
                            tab: selectedTab,
                            defaultFilters: overrideFilters,
                        })}
                    />
                )}

                {selectedTab === ExplorerTopLevelEntity.CLERK && (
                    <ExplorerViewClerk
                        {...commonTabProps}
                        onFiltersChange={(value) => {
                            setAppliedFilters({
                                ...queryFilters,
                                clerk: value,
                            });
                        }}
                        key={selectedWindow}
                    />
                )}

                {selectedTab === ExplorerTopLevelEntity.INDUSTRY && (
                    <ExplorerViewIndustry
                        {...commonTabProps}
                        onFiltersChange={(value) => {
                            setAppliedFilters({
                                ...queryFilters,
                                industry: value,
                            });
                        }}
                        key={selectedWindow}
                    />
                )}

                {selectedTab === ExplorerTopLevelEntity.PRODUCT && (
                    <ExplorerViewProduct
                        {...commonTabProps}
                        onFiltersChange={(value) => {
                            setAppliedFilters({
                                ...queryFilters,
                                product: value,
                            });
                        }}
                        key={selectedWindow}
                    />
                )}

                {selectedTab === ExplorerTopLevelEntity.PRODUCT_GROUP && (
                    <ExplorerViewProductGroup
                        {...commonTabProps}
                        onFiltersChange={(value) => {
                            setAppliedFilters({
                                ...queryFilters,
                                productGroup: value,
                            });
                        }}
                        key={selectedWindow}
                    />
                )}

                {selectedTab === ExplorerTopLevelEntity.NORMALISED_CATEGORY &&
                    appConfig.normalisedCategoryDepthsUsedInInsightsInExplorer !== null && (
                        <ExplorerViewNormalisedCategory
                            {...commonTabProps}
                            onFiltersChange={(value) => {
                                setAppliedFilters({
                                    ...queryFilters,
                                    normalisedCategory: value,
                                });
                            }}
                            key={selectedWindow}
                        />
                    )}

                {selectedTab === ExplorerTopLevelEntity.SUPPLIER && (
                    <ExplorerViewSupplier
                        {...commonTabProps}
                        onFiltersChange={(value) => {
                            setAppliedFilters({
                                ...queryFilters,
                                supplier: value,
                            });
                        }}
                        key={selectedWindow}
                    />
                )}
            </div>
        </div>
    );
}
